<template>
  <gp-alert alert-type="primary" outline data-testid="pending-restart">
    <div class="flex flex-col gap-3">
      <div>
        <h6 class="mb-0 text-xl font-bold">
          {{ t(`ngp/wait_until_empty/banner/${pendingRestart.rule === "update" ? "update" : "restart"}/title`) }}
        </h6>
        <p class="mb-0">
          {{
            pendingRestart.players === null || pendingRestart.players === undefined
              ? t("ngp/wait_until_empty/banner/text_unknown", { time: deadline })
              : t("ngp/wait_until_empty/banner/text", { players: pendingRestart.players, time: deadline })
          }}
        </p>
      </div>
      <div>
        <gp-btn
          btn-type="primary"
          size="small"
          :label="t('ngp/wait_until_empty/banner/restart_now')"
          :disabled="restarting"
          data-testid="pending-restart-now"
          @click="restartNow"
        />
      </div>
    </div>
  </gp-alert>
</template>

<script setup lang="ts">
/**
 * A restart the daemon holds until the server is empty (TR-5485). Fed live by the service state subscription, so
 * the player count updates and the banner disappears by itself once the restart happens. Unlike a broadcast
 * countdown it never locks the panel. "Restart now" is a forced restart, the only way to skip the wait.
 */
import type { PendingRestart } from "@/graphql";
import { useServiceStore } from "@/store/service";
import { GpAlert, GpBtn } from "@gportal/uikit";
import { DateTime } from "luxon";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ pendingRestart: PendingRestart }>();

const { t, locale } = useI18n();
const service = useServiceStore();
const restarting = ref(false);

// In the panel's language, not the browser's: the texts around it are ("um 14:30 Uhr", "at 2:30 PM").
const deadline = computed(() =>
  DateTime.fromSeconds(props.pendingRestart.deadline).setLocale(locale.value).toLocaleString(DateTime.TIME_SIMPLE),
);

async function restartNow() {
  const rsid = service.ctx.service?.config?.rsid;
  if (!rsid) return;
  restarting.value = true;
  try {
    await service.restartService(rsid, true);
  } finally {
    restarting.value = false;
  }
}
</script>
